import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { PlusIcon, SearchIcon, PeopleIcon } from '../customer/CustomersUI';
import { COPY, vendorBalanceColor } from './vendors.copy';

/**
 * The left column: every supplier, searchable.
 *
 * The customers list, with the sentence reversed. There the figure is what a
 * person owes the shop; here it is what the shop owes a supplier, so the
 * footer reads "Total owing" rather than "Total owed" and the colour rule is
 * its own — see `vendorBalanceColor`.
 *
 * The subtitle is `phone · agent`, matching the phone's VendorTile, because
 * the agent is the human somebody actually rings.
 */

function VendorRow({ vendor, currency, selected, onOpen }) {
  const [hovered, setHovered] = React.useState(false);

  const subtitle = [vendor.phone, vendor.agent].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={vendor.name}
      onClick={() => onOpen(vendor)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(vendor);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 20px',
        background: selected ? T.selectedBg : hovered ? T.hover : 'transparent',
        // The stripe replaces the tint on the left edge so a selected row is
        // still obvious once the list is scrolled and colours repeat.
        borderLeft: `3px solid ${selected ? T.brand : 'transparent'}`,
        borderBottom: `1px solid ${T.hairline}`,
        cursor: 'pointer',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: T.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {vendor.name}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: T.inkFaint,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle || '—'}
        </span>
      </div>

      <span
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: vendorBalanceColor(vendor.balance),
          ...NUMERIC,
        }}
      >
        {money(vendor.balance, currency)}
      </span>
    </div>
  );
}

export default function VendorList({
  vendors,
  currency,
  query,
  onQueryChange,
  selectedId,
  onOpen,
  onAdd,
  loading,
}) {
  // Only outstanding bills count toward the total. A supplier the shop has
  // overpaid is money owed BACK to the shop, and netting the two would
  // understate what is actually due out.
  const owing = vendors.reduce((sum, vendor) => {
    const balance = Number(vendor.balance || 0);
    return balance > 0 ? sum + balance : sum;
  }, 0);

  return (
    <aside
      style={{
        width: T.listWidth,
        flexShrink: 0,
        height: '100%',
        background: T.surface,
        borderRight: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '20px 20px 16px',
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
            {COPY.title}
          </span>
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 13px',
              background: T.selectedBg,
              border: `1px solid ${T.selectedBorder}`,
              borderRadius: T.radiusControl,
              font: 'inherit',
              color: T.brandInk,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <PlusIcon />
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{COPY.add}</span>
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            height: 40,
            padding: '0 13px',
            border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            boxSizing: 'border-box',
          }}
        >
          <span style={{ display: 'flex', color: T.inkFaint }}>
            <SearchIcon />
          </span>
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={COPY.search}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              font: 'inherit',
              fontSize: 13.5,
              color: T.ink,
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>{COPY.count(vendors.length)}</span>
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>
            {COPY.totalOwing}{' '}
            <strong style={{ fontWeight: 700, color: T.brandInk, ...NUMERIC }}>
              {money(owing, currency)}
            </strong>
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {vendors.length ? (
          vendors.map((vendor) => (
            <VendorRow
              key={vendor._id}
              vendor={vendor}
              currency={currency}
              selected={vendor._id === selectedId}
              onOpen={onOpen}
            />
          ))
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 40,
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 52,
                height: 52,
                border: `1.5px dashed ${T.borderStrong}`,
                borderRadius: T.radiusPill,
                color: T.inkGhost,
              }}
            >
              <PeopleIcon size={22} />
            </span>
            <span style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>
              {loading ? COPY.loading : COPY.noneFound}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}
